import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import BidTop from "./BidTop";
import { BidsGridSkeleton } from "./BiditSkeletons";
import PaginationRounded from "./PaginationRounded";
import ShowPosts from "./ShowPosts";

const initialFilter = { area: "הכל​", profession: "הכל​" };

function Feed({ search1 }) {
  const [filter, setFilter] = useState(initialFilter);
  const [allPosts, setAllPosts] = useState([]);
  const [activeView, setActiveView] = useState("all");
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const token = Cookies.get("token");
    axios
      .get(`${process.env.React_APP_BACKEND_URL}/api/allbids`, {
        headers: { Authorization: `Bearer ${token || ""}` },
      })
      .then((res) => {
        setAllPosts((res.data?.Bids || []).filter((post) => post.showbid !== "no"));
        setShow(true);
      })
      .catch(() => setShow(true));
  }, []);

  const now = Date.now();
  const activePosts = useMemo(
    () => allPosts.filter((post) => Number(post.deadline) > now),
    [allPosts, now]
  );
  const endedPosts = useMemo(
    () => allPosts.filter((post) => Number(post.deadline) <= now),
    [allPosts, now]
  );

  const visibleActivePosts = useMemo(() => {
    if (activeView === "ending") {
      return activePosts.filter((post) => Number(post.deadline) - now <= 18 * 60 * 60 * 1000);
    }
    if (activeView === "new") {
      return activePosts.filter((post) => now - new Date(post.created_at).getTime() <= 72 * 60 * 60 * 1000);
    }
    return activePosts;
  }, [activePosts, activeView, now]);

  const handleActiveView = (view) => {
    setActiveView(view);
    setPage(1);
  };

  const scrollToAssistant = () => {
    document.getElementById("bidit-tender-assistant")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box flex={3} sx={{ fontFamily: process.env.REACT_APP_FONT, width: "100%", minWidth: 0, p: { xs: 0.5, sm: 1, md: 1.5 } }}>
      <BidTop
        hideIntro
        showFilters={activePosts.length > 0}
        setFilter={setFilter}
        filter={filter}
        activeView={activeView}
        handleAll={() => handleActiveView("all")}
        handleEnding={() => handleActiveView("ending")}
        handleNewlyListed={() => handleActiveView("new")}
      />

      {search1 !== "no" && (
        <Box sx={{ mb: 2, px: 2, py: 1.25, borderRadius: "14px", backgroundColor: "#EEF7FF", border: "1px solid #D7EAF9" }}>
          <Typography sx={{ fontFamily: "Assistant", color: "#214B69", fontWeight: 700 }}>
            תוצאות החיפוש עבור "{search1}"
          </Typography>
        </Box>
      )}

      {!show && <BidsGridSkeleton count={6} />}

      {show && activePosts.length > 0 && (
        <Box component="section" aria-labelledby="active-tenders-title" sx={{ mb: { xs: 3.5, md: 4.5 } }}>
          <Typography id="active-tenders-title" component="h2" sx={{ mb: 1.6, fontFamily: "Assistant", fontWeight: 900, fontSize: { xs: 22, md: 27 }, color: "#173B57" }}>
            מכרזים פעילים עכשיו
          </Typography>
          <ShowPosts showPosts={visibleActivePosts} page={page} filter={filter} what="all" search1={search1} />
          {visibleActivePosts.length > 18 && (
            <PaginationRounded handleChange={(_, value) => setPage(value)} page={page} count={Math.ceil(visibleActivePosts.length / 18)} />
          )}
        </Box>
      )}

      {show && activePosts.length === 0 && (
        <Box component="section" dir="rtl" sx={{ mb: { xs: 3.5, md: 4.5 }, px: { xs: 1.6, sm: 2.5 }, py: { xs: 2.5, md: 3.2 }, textAlign: "center", borderRadius: "22px", bgcolor: "#FFFFFF", border: "1px solid #DCE9F3", boxShadow: "0 10px 28px rgba(27,77,114,.07)" }}>
          <Typography component="h2" sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", fontSize: { xs: 21, md: 26 } }}>
            אין כרגע מכרזים פעילים
          </Typography>
          <Typography sx={{ mt: 0.6, mb: 2, fontFamily: "Assistant", color: "#627B8D", fontSize: { xs: 14.5, md: 16 } }}>
            יש עבודה שצריך לבצע? המכרז הבא יכול להיות שלכם.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
            <Button variant="contained" onClick={scrollToAssistant} sx={{ minHeight: 46, px: 2.6, borderRadius: "12px", fontFamily: "Assistant", fontWeight: 900, textTransform: "none", bgcolor: "#F28C18", boxShadow: "0 7px 18px rgba(242,140,24,.22)", "&:hover": { bgcolor: "#DD7B0B" } }}>
              פרסמו עבודה בחינם
            </Button>
            <Button component={NavLink} to="/proflandingpage" variant="outlined" startIcon={<HandymanRoundedIcon />} sx={{ minHeight: 46, px: 2.3, borderRadius: "12px", fontFamily: "Assistant", fontWeight: 900, textTransform: "none", "& .MuiButton-startIcon": { ml: 0.6, mr: 0 } }}>
              הרשמה לבעלי מקצוע
            </Button>
          </Stack>
        </Box>
      )}

      {show && endedPosts.length > 0 && (
        <Box component="section" aria-labelledby="ended-tenders-title" sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.6 }}>
            <Typography id="ended-tenders-title" component="h2" sx={{ fontFamily: "Assistant", fontWeight: 900, fontSize: { xs: 21, md: 26 }, color: "#173B57" }}>
              מכרזים שהסתיימו לאחרונה
            </Typography>
            <Button
              component={NavLink}
              to="/ended-bids"
              endIcon={<ArrowBackRoundedIcon />}
              sx={{ flexShrink: 0, fontFamily: "Assistant", fontWeight: 900, textTransform: "none", borderRadius: "10px", "& .MuiButton-endIcon": { mr: 0.5, ml: 0 } }}
            >
              לכל המכרזים שהסתיימו
            </Button>
          </Stack>
          <ShowPosts
            showPosts={endedPosts.slice(-3)}
            page={1}
            filter={initialFilter}
            what="closed"
            search1={search1}
          />
        </Box>
      )}
    </Box>
  );
}

export default Feed;
